import { entitySlugFor, isSearchableSlug } from '@/composables/entityColumns';

/**
 * Which records a query holds, so that it is fetched again when the backend announces that one
 * of their search documents was written (SAU-530). The backend announces a kind of record by its
 * slug, the name its search route carries, such as `location_zone_custom_playlist`.
 */
export interface LiveRecordsMeta {
    slug: string;
    ids: string[];
}

declare module '@tanstack/vue-query' {
    interface Register {
        queryMeta: {
            liveRecords?: LiveRecordsMeta;
        };
    }
}

/**
 * The `meta` a query declares to join the live updates: the kind of record, as a class basename
 * (`LocationZoneCustomPlaylist`) or a slug (`location_zone_custom_playlist`), and the ids it holds.
 * A query that declares this is refetched, in place, when one of those records is announced
 * ({@link installSearchDocumentAnnouncements}).
 */
export function liveRecords(entityOrSlug: string, ids: Iterable<unknown>): { liveRecords: LiveRecordsMeta } | undefined {
    const slug = entitySlugFor(entityOrSlug);
    // Only a kind with search documents is announced. The backend refuses any other channel.
    if (!slug || !isSearchableSlug(slug)) return undefined;
    const recordIds = [...ids].filter((id) => id !== null && id !== undefined && id !== '').map(String);
    return { liveRecords: { slug, ids: recordIds } };
}
